import React, { useState, useEffect } from "react";
import { Row, Col, Table, Space, Button, Input, notification } from "antd";
import moment from "moment";
import TmsShiftService from "../../../services/tms-shift.service";
import TmsSevasService from "../../../services/tms-sevas.service";
import TmsDarshanamService from "../../../services/tms-darshanam/tms-darshanam.service";
import TmsDonationService from "../../../services/tms-donation.service";
import TmsAccommodationService from "../../../services/tms-accommodation.service";
import TmsPrasadamService from "../../../services/tms-prasadam.service";
import catchErrorFunction from "../../../utils/tms-catch-error/tms-catch-error";
import { withRouter } from "../../../utils/tms-with-router/tms-with-router";
import TmsDatePickerRange from "../../../components/tms-customs/tms-datepicker-range";
import TmsSelect from "../../../components/tms-customs/tms-select";
import { exportExcel } from "../../../utils/tms-excel/tms-excel";
import TmsExcelButton from "../../../components/tms-customs/tms-excel-button";

const TmsReportsScanLogs = ({ router }) => {
  const [dataLoading, setDataLoading] = useState(false);
  const [reportData, setReportData] = useState("");
  const [tableTotalSize, setTabelTotalSize] = useState(0);
  const [tableCurrentPage, setTabelCurrentPage] = useState(1);
  const [tablePageSize, setTabelPageSize] = useState(15);
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [outcome, setOutcome] = useState("");
  const [productType, setProductType] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [productList, setProductList] = useState([]);
  const [productListLoading, setProductListLoading] = useState(false);

  const productMap = {
    counter_seva: "Sevas",
    counter_darshanam: "Darshanam",
    counter_donation: "Donation",
    counter_accommodation: "Accommodation",
    counter_prasadam: "Prasadam",
    counter_misc_product: "Miscellaneous",
    counter_thulabharam: "Thulabharam",
    counter_tollgate: "Tollgate",
    counter_kalyanakatta: "Kalyanakatta",
  };

  const topLevelProductMapping = {
    Darshanam: ["counter_darshanam", "online_darshanam"],
    Seva: ["counter_seva", "online_seva"],
    Donation: ["counter_donation", "online_donation"],
    Prasadam: ["counter_prasadam"],
    Tollgate: ["counter_tollgate"],
    Thulabharam: ["counter_thulabharam"],
    Accommodation: [
      "counter_accommodation",
      "online_accommodation",
      "counter_accommodation_update",
      "counter_accommodation_advance",
    ],
    Kalyanakatta: ["counter_kalyanakatta"],
    "Manual Entry": ["counter_manual_entry"],
    "Shop Product": ["counter_shop_product", "online_shop_product"],
    "Misc Product": ["counter_misc_product"],
    Publication: ["counter_publication"],
    Hundi: ["counter_hundi"],
    "Protocol Request": ["protocol_request"],
  };

  const typesWithProductSelect = [
    "Seva",
    "Darshanam",
    "Donation",
    "Accommodation",
    "Prasadam",
  ];

  const productParamKeyMap = {
    Seva: "selectedSevaId",
    Darshanam: "selectedDarshanamId",
    Donation: "selectedDonationId",
    Prasadam: "selectedPrasadamId",
    Accommodation: "selectedAccBlockId",
  };

  const getTopLevelLabel = (productTypeValue) => {
    for (const [label, values] of Object.entries(topLevelProductMapping)) {
      if (values.join(",") === productTypeValue) return label;
    }
    return null;
  };

  const fetchProductList = async (productTypeValue) => {
    const label = getTopLevelLabel(productTypeValue);
    if (!label || !typesWithProductSelect.includes(label)) {
      setProductList([]);
      return;
    }
    setProductListLoading(true);
    try {
      let data;
      switch (label) {
        case "Seva":
          data = await TmsSevasService.getSevas(1, 200);
          break;
        case "Darshanam":
          data = await TmsDarshanamService.getDarshanam(1, 200);
          break;
        case "Donation":
          data = await TmsDonationService.getDonations(1, 200);
          break;
        case "Accommodation":
          data = await TmsAccommodationService.getAllBlocks(1, 200);
          break;
        case "Prasadam":
          data = await TmsPrasadamService.getPrasadam(1, 200);
          break;
        default:
          data = { items: [] };
      }
      setProductList(data?.items ?? []);
    } catch (error) {
      catchErrorFunction(error, "Failed to fetch product list.", router);
      setProductList([]);
    }
    setProductListLoading(false);
  };

  const buildSelectedProductParam = () => {
    const label = getTopLevelLabel(productType);
    if (!label || !productParamKeyMap[label] || !selectedProductId) return undefined;
    return { key: productParamKeyMap[label], value: selectedProductId };
  };

  const shouldShowProductSelect =
    productType !== "" &&
    typesWithProductSelect.includes(getTopLevelLabel(productType));

  const fetchAllTransactions = async () => {
    setDataLoading(true);
    try {
      setReportData("");
      const data = await TmsShiftService.getAllScanLogs(
        tableCurrentPage,
        tablePageSize,
        toDate,
        fromDate,
        ticketId,
        outcome,
        productType,
        buildSelectedProductParam()
      );
      setReportData(data?.items);
      setTabelTotalSize(data?.meta?.totalItems);
    } catch (error) {
      catchErrorFunction(error, "Token expired! Please login again.", router);
    }
    setDataLoading(false);
  };

  useEffect(() => {
    fetchAllTransactions();
  }, [tableCurrentPage, tablePageSize]);

  // eslint-disable-next-line no-unused-vars
  const handleTableChange = (pagination, filters, sorter) => {
    setTabelCurrentPage(pagination.current);
    setTabelPageSize(pagination.pageSize);
  };

  const columns = [
    {
      title: "id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
    },
    {
      title: "POS Name",
      dataIndex: "counterName",
      key: "counterName",
    },
    {
      title: "Seva Type",
      dataIndex: "productType",
      render: (record) => {
        return productMap[record] ?? record;
      },
    },
    {
      title: "Seva Name",
      dataIndex: 'productName'
    },
    { title: "Ticket Id", dataIndex: "ticketId", key: "ticketId" },

    {
      title: "Order Date - Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("DD-MM-YYYY hh:mm A"),
    },

    { title: "outcome", dataIndex: "outcome", key: "outcome" },
    {
      title: "Failure reason",
      dataIndex: "failureReason",
      render: (record) => record ?? "-",
    },
  ];
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      let data = await TmsShiftService.getAllScanLogs(
        1,
        10000,
        toDate,
        fromDate,
        ticketId,
        outcome,
        productType,
        buildSelectedProductParam()
      );
      if (data.items.length === 0) {
        notification.info({ message: "No Data" });
        setExcelLoading(true);
      }
      data = data?.items?.reverse();
      const compiledData = data.map((item, i) => {
        return {
          "S.no": i + 1,
          Counter: `${item.counterId} - ${item.counterName}`,
          Service: item.productType.replaceAll("_", " "),
          ServiceName: item.productName,
          "Ticket ID": item.ticketId,
          Date: moment(item.createdAt).format("DD-MM-YYYY"),
          Outcome: item.outcome,
          "Failure Reason": item.failureReason,
          User: `${item.userId} - ${item.displayName}`,
          "C.S.LoginLogId": item.counterShiftLoginLogId,
        };
      });
      exportExcel(
        compiledData,
        `scan-logs-${moment().format("DD-MM-YYYY")}`,
        setExcelLoading
      );
    } catch (error) {
      catchErrorFunction(error, "Token expired!! Please login again.", router);
      setExcelLoading(false);
    }
  };
  return (
    <>
      <Row style={{ marginBottom: "20px", display: "flex" }} gutter={[20, 20]}>
        <Col>
          <TmsDatePickerRange
            label="Select Duration"
            to={toDate}
            from={fromDate}
            setFrom={setFromDate}
            setTo={setToDate}
          />
        </Col>

        <Col style={{ padding: "0" }}>
          <p style={{ marginBottom: "5px", textAlign: "left" }}>Ticket ID</p>
          <Input
            maxLength={220}
            value={ticketId}
            style={{ width: "200px" }}
            onChange={(e) => setTicketId(e.target.value)}
          />
        </Col>
        <TmsSelect
          type={"col"}
          span={4}
          label="Status"
          value={outcome}
          setValue={setOutcome}
          data={[
            { name: "Success", id: "SUCCESS" },
            { name: "Failure", id: "FAILURE" },
          ]}
        />
        <TmsSelect
          type={"col"}
          span={4}
          label="Product Type"
          value={productType}
          setValue={(val) => {
            setProductType(val);
            setSelectedProductId("");
            setProductList([]);
            if (val !== "") {
              fetchProductList(val);
            }
          }}
          data={Object.entries(topLevelProductMapping).map(
            ([name, values]) => ({
              name,
              id: values.join(","),
            })
          )}
        />
        {shouldShowProductSelect && (
          <TmsSelect
            type={"col"}
            span={4}
            label="Select Product"
            value={selectedProductId}
            setValue={setSelectedProductId}
            data={productList.map((item) => ({
              id: item.id,
              name: item.name,
            }))}
            disabled={productListLoading}
          />
        )}

        <Col
          style={{
            maxWidth: "100px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <Button type="primary" onClick={() => fetchAllTransactions()}>
            Fetch
          </Button>
        </Col>
        <TmsExcelButton
          span={24}
          loading={excelLoading}
          handleExcelExport={handleExcelExport}
          top={true}
        />
      </Row>

      <Row>
        <Space size="small">
          <a>{tableTotalSize}</a>
          <span>Scan Logs</span>
        </Space>
      </Row>
      <Table
        style={{ width: "100%", textTransform: "capitalize" }}
        columns={columns}
        dataSource={reportData}
        loading={dataLoading}
        size="small"
        scroll={{ x: true }}
        pagination={{
          total: tableTotalSize,
          current: tableCurrentPage,
          pageSize: tablePageSize,
        }}
        onChange={handleTableChange}
      ></Table>
    </>
  );
};

export default withRouter(TmsReportsScanLogs);
